import * as React from 'react';
import * as ReactDOM from 'react-dom';

export interface IMyWindowPortal {
  title: string;
  closeWindowPortal: () => void;
  moveFromElement?: string;
  moveToElement?: string;
  size?: string;
}

export default class MyWindowPortal extends React.Component<IMyWindowPortal> {
  containerEl: HTMLDivElement;
  externalWindow: any;
  beforeUnloadHandler: () => void;
  constructor(props) {
    super(props);
    this.containerEl = document.createElement('div'); // STEP 1: create an empty div
    this.externalWindow = null;
    this.beforeUnloadHandler = this.onBeforeUnload;
  }

  onBeforeUnload = () => {
    if (!this.externalWindow) {
      return;
    }

    if (this.containerEl.parentNode === this.externalWindow.document.body) {
      this.externalWindow.document.body.removeChild(this.containerEl);
    }

    this.props.closeWindowPortal();
  };

  copyStyles(sourceDoc, targetDoc) {
    Array.from(sourceDoc.styleSheets).forEach((styleSheet) => {
      if ((styleSheet as any).href) {
        // true for stylesheets loaded from a URL
        const newLinkEl = sourceDoc.createElement('link');

        newLinkEl.rel = 'stylesheet';
        newLinkEl.href = (styleSheet as any).href;
        targetDoc.head.appendChild(newLinkEl);
      } else if ((styleSheet as any).cssRules) {
        // true for inline styles
        const newStyleEl = sourceDoc.createElement('style');

        Array.from((styleSheet as any).cssRules).forEach((cssRule) => {
          newStyleEl.appendChild(
            sourceDoc.createTextNode((cssRule as any).cssText)
          );
        });

        targetDoc.head.appendChild(newStyleEl);
      }
    });
  }

  componentDidMount() {
    const size = this.props.size || 'width=1400,height=950,left=220,top=20';
    // STEP 3: open a new browser window and store a reference to it
    this.externalWindow = window.open('', '', size);
    if (!this.externalWindow) {
      return;
    }

    // STEP 4: append the container <div> (that has props.children appended to it) to the body of the new window

    this.externalWindow.document.body.appendChild(this.containerEl);

    this.externalWindow.document.title = this.props.title;
    this.copyStyles(document, this.externalWindow.document);

    if (this.props.moveFromElement && this.props.moveToElement) {
      const popupWindow = this.externalWindow;
      const moveFromSelector = this.props.moveFromElement;
      const moveToSelector = this.props.moveToElement;

      (async () => {
        while (
          this.externalWindow === popupWindow &&
          !popupWindow.closed &&
          popupWindow.document.querySelector(moveFromSelector) === null
        ) {
          await new Promise((resolve) => requestAnimationFrame(resolve));
        }

        if (this.externalWindow !== popupWindow || popupWindow.closed) {
          return;
        }

        const moveToElement = popupWindow.document.querySelector(moveToSelector);
        const moveFromElement = popupWindow.document.querySelector(moveFromSelector);

        if (moveToElement && moveFromElement) {
          moveToElement.appendChild(moveFromElement);
        }
      })();
    }

    // update the state in the parent component if the user closes the
    // new window
    this.externalWindow.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  handleWindowClose = () => {
    if (!this.externalWindow) {
      return;
    }

    this.externalWindow.removeEventListener('beforeunload', this.beforeUnloadHandler);
    if (!this.externalWindow.closed) {
      this.externalWindow.close();
    }
    this.externalWindow = null;
  };

  componentWillUnmount() {
    // This will fire when this.state.showWindowPortal in the parent component becomes false
    // So we tidy up by just closing the window.
    this.handleWindowClose();
  }

  render() {
    // STEP 2: append props.children to the container <div> that isn't mounted anywhere yet
    return ReactDOM.createPortal(this.props.children, this.containerEl);
  }
}
